import React, { createContext, useContext, useEffect, useMemo } from 'react';
import type { IMessage } from '@stomp/stompjs';
import { useWebSocket as useStompSocket } from '@/hooks/use-stomp-socket';

type MessageHandler = (message: IMessage) => void;

type ChatStompContextValue = {
  subscribe: (destination: string, handler: MessageHandler) => (() => void) | undefined;
  publish: (destination: string, body: unknown) => void;
  isConnected: boolean;
};

const ChatStompContext = createContext<ChatStompContextValue | null>(null);

export function ChatStompProvider({ children }: { children: React.ReactNode }) {
  const { connect, disconnect, subscribe, publish, isConnected } = useStompSocket();

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  const value = useMemo<ChatStompContextValue>(
    () => ({
      subscribe,
      publish,
      isConnected,
    }),
    [subscribe, publish, isConnected]
  );

  return <ChatStompContext.Provider value={value}>{children}</ChatStompContext.Provider>;
}

export function useChatStomp() {
  const context = useContext(ChatStompContext);
  if (!context) {
    throw new Error('useChatStomp must be used within a ChatStompProvider');
  }
  return context;
}
